import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import recommendationService from '../../services/recommendationService';
import { useModules } from '../../context/LevelsContext';

import RecommendationIcon from '../../components/ui/icons/RecommendationIcon';

// Icons

const CheckIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
);

const XIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const FileIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
);

const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
    </div>
);

// Sub-components
const ConditionCard = ({ label, met, progressText, icon }) => (
    <div className={`rounded-2xl p-5 border-2 transition-colors ${
        met
            ? 'bg-green-50 dark:bg-green-500/5 border-green-300 dark:border-green-500/30'
            : 'bg-red-50 dark:bg-red-500/5 border-red-300 dark:border-red-500/30'
    }`}>
        <div className="flex items-start gap-3">
            <div className={`p-1.5 rounded-full mt-0.5 ${met ? 'bg-green-500' : 'bg-red-500'}`}>
                {met
                    ? <CheckIcon className="w-4 h-4 text-white" />
                    : <XIcon className="w-4 h-4 text-white" />
                }
            </div>
            <div className="flex-1 min-w-0">
                <p className={`font-bold text-sm ${met ? 'text-green-800 dark:text-green-400' : 'text-red-800 dark:text-red-400'}`}>
                    {label}
                </p>
                <p className={`text-xs mt-1 ${met ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}`}>
                    {progressText}
                </p>
            </div>
            {icon && <div className="text-gray-300 dark:text-gray-600">{icon}</div>}
        </div>
    </div>
);

const FormField = ({ label, value, onChange, type = "text", required = false, placeholder = "" }) => (
    <div className="space-y-2">
        <label className="text-[11px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest ml-1">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-2xl px-5 py-4 text-gray-900 dark:text-white font-medium focus-within:border-green-500/50 transition-colors">
            <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className="bg-transparent w-full outline-none placeholder-gray-400"
            />
        </div>
    </div>
);

const RecommendationLetter = ({ levelVar: fixedLevel, onClose } = {}) => {
    const { user } = useAuth();
    const { language } = useLanguage();
    const isModal = !!onClose;

    // State
    const [selectedLevel, setSelectedLevel] = useState(fixedLevel || '');
    const [loading, setLoading] = useState(false);
    const [eligibility, setEligibility] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Page mode overview state
    const [showFormModal, setShowFormModal] = useState(false);
    const [allLetters, setAllLetters] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Form
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [medicalCertNumber, setMedicalCertNumber] = useState('');
    const [hasValidPermit, setHasValidPermit] = useState(false);
    const [flyingHours, setFlyingHours] = useState('');
    const [letterType, setLetterType] = useState('');
    const [selectedModules, setSelectedModules] = useState([]);
    const { modules } = useModules();

    // All levels that support the recommendation feature (from catalog)
    const compatibleLevels = modules.filter(m => m.hasRecommendation && !m.isPack);

    // Build available levels with subscription status
    const availableLevels = compatibleLevels.map(level => {
        const activeData = user?.activeLevels?.[level.id];
        const isActive = activeData && activeData.remainingDays > 0;
        return [level.id, {
            ...level,
            labelFr: level.title,
            labelEn: level.titleObj?.en || level.title,
            isActive,
            remainingDays: activeData?.remainingDays || 0,
            hasRecommendation: true
        }];
    });

    // Color lookup
    const getLevelColor = (code) => {
        const mod = modules.find(m => m.id === code);
        return mod?.newBackgroundColor || '#6B7280';
    };

    // Label lookup for history display
    const getLevelLabel = (levelVar) => {
        const mod = modules.find(m => m.id === levelVar);
        return mod?.title || levelVar?.toUpperCase() || '?';
    };

    // Whether content is rendered inside a modal overlay
    const renderInModal = isModal || showFormModal;

    // Auto-select first level (skip if fixedLevel provided)
    useEffect(() => {
        if (!fixedLevel && availableLevels.length > 0 && !selectedLevel) {
            setSelectedLevel(availableLevels[0][0]);
        }
    }, [user?.activeLevels]);

    // Pre-fill form from user profile
    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
        }
    }, [user]);

    // Page mode: fetch all letters history
    useEffect(() => {
        if (isModal) return;

        const fetchHistory = async () => {
            setHistoryLoading(true);
            try {
                const letters = await recommendationService.getHistory();
                setAllLetters(letters);
            } catch (err) {
                console.error('Failed to fetch letter history:', err);
            } finally {
                setHistoryLoading(false);
            }
        };
        fetchHistory();
    }, [isModal]);

    // Fetch eligibility when level changes - only in modal contexts
    useEffect(() => {
        if (!selectedLevel) return;
        if (!isModal && !showFormModal) return;

        const fetchEligibility = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await recommendationService.checkEligibility(selectedLevel);
                setEligibility(data);
                // Pre-fill from returned user data if local is empty
                if (!firstName && data.user?.firstName) setFirstName(data.user.firstName);
                if (!lastName && data.user?.lastName) setLastName(data.user.lastName);
            } catch (err) {
                setError(language === 'fr' ? 'Erreur lors de la vérification' : 'Error checking eligibility');
            } finally {
                setLoading(false);
            }
        };

        fetchEligibility();
    }, [selectedLevel, isModal, showFormModal]);

    // Available letter types from backend
    const availableTypes = eligibility?.availableTypes || [];
    const availableModules = eligibility?.availableModules || [];

    // Auto-select letter type when eligibility changes
    useEffect(() => {
        if (availableTypes.length > 0 && !availableTypes.includes(letterType)) {
            setLetterType(availableTypes[0]);
            setSelectedModules([]);
        }
    }, [eligibility]);

    // Form validation
    const modulesValid = letterType !== 'nivels' || selectedModules.length > 0;
    const formValid = firstName.trim() && lastName.trim() && medicalCertNumber.trim() && letterType && modulesValid;

    // Generate handler
    const handleGenerate = async () => {
        if (!formValid || !eligibility?.eligible) return;

        setSubmitting(true);
        setError(null);
        try {
            const result = await recommendationService.generate({
                levelId: selectedLevel,
                type: letterType,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                medicalCertNumber: medicalCertNumber.trim(),
                hasValidPermit,
                flyingHours: parseInt(flyingHours) || 0,
                moduleIds: letterType === 'nivels' ? selectedModules : null
            });

            if (result.success && result.letterId) {
                // Open letter in new tab
                const url = recommendationService.getViewUrl(result.letterId);
                window.open(url, '_blank');

                // Refresh eligibility to update history
                const updated = await recommendationService.checkEligibility(selectedLevel);
                setEligibility(updated);

                // Refresh page overview history if in page form modal
                if (showFormModal) {
                    const letters = await recommendationService.getHistory();
                    setAllLetters(letters);
                }
            }
        } catch (err) {
            setError(language === 'fr' ? 'Erreur lors de la génération' : 'Error generating letter');
        } finally {
            setSubmitting(false);
        }
    };

    // View existing letter
    const handleViewLetter = (letterId) => {
        const url = recommendationService.getViewUrl(letterId);
        window.open(url, '_blank');
    };

    // Close form modal and refresh history
    const handleCloseFormModal = async () => {
        setShowFormModal(false);
        setEligibility(null);
        try {
            const letters = await recommendationService.getHistory();
            setAllLetters(letters);
        } catch (err) {
            console.error('Failed to refresh history:', err);
        }
    };

    const t = {
        title: language === 'fr' ? 'Lettre de recommandation' : 'Recommendation Letter',
        subtitle: language === 'fr'
            ? 'Génère ta lettre officielle pour Transport Canada'
            : 'Generate your official letter for Transport Canada',
        selectLevel: language === 'fr' ? 'Sélectionner un niveau' : 'Select a level',
        noLevels: language === 'fr'
            ? 'Aucun niveau compatible avec cette fonctionnalité.'
            : 'No levels compatible with this feature.',
        conditionsTitle: language === 'fr' ? 'Conditions requises' : 'Required Conditions',
        formTitle: language === 'fr' ? 'Mes informations' : 'My Information',
        firstName: language === 'fr' ? 'Prénom' : 'First Name',
        lastName: language === 'fr' ? 'Nom' : 'Last Name',
        medicalCert: language === 'fr' ? 'N° de licence ou de certificat médical' : 'Licence number or medical certificate number',
        validPermit: language === 'fr' ? 'Je détiens un permis de vol valide' : 'I hold a valid flying permit',
        flyingHours: language === 'fr' ? 'Heures de vol' : 'Flying hours',
        generate: language === 'fr' ? 'Générer ma lettre' : 'Generate my letter',
        generating: language === 'fr' ? 'Génération en cours...' : 'Generating...',
        historyTitle: language === 'fr' ? 'Lettres générées' : 'Generated Letters',
        view: language === 'fr' ? 'Voir' : 'View',
        notEligible: language === 'fr'
            ? 'Complète toutes les conditions ci-dessus pour générer ta lettre.'
            : 'Complete all conditions above to generate your letter.',
        cond1: language === 'fr' ? 'Chapitres complétés' : 'Chapters completed',
        cond2: language === 'fr' ? 'Heures de formation minimum' : 'Minimum training hours',
        cond3: language === 'fr' ? 'Tous les examens passés' : 'All exams attempted',
        cond4: language === 'fr' ? '3+ examens avec score ≥ 80%' : '3+ exams with score ≥ 80%',
        typeComplet: language === 'fr' ? 'Complète (première demande)' : 'Complete (first request)',
        typeNieme: language === 'fr' ? 'Reprise d\'examen' : 'Exam retake',
        typeNivels: language === 'fr' ? 'Partielle (modules spécifiques)' : 'Partial (specific modules)',
        letterType: language === 'fr' ? 'Type de lettre' : 'Letter type',
        selectModules: language === 'fr' ? 'Modules à repasser' : 'Modules to retake',
        niemeNotReady: language === 'fr'
            ? 'Reprise : nécessite 3 examens ≥ 80% depuis la dernière lettre'
            : 'Retake: requires 3 exams ≥ 80% since last letter',
        niemeProgress: language === 'fr'
            ? (n) => `${n}/3 examens ≥ 80% depuis la dernière lettre`
            : (n) => `${n}/3 exams ≥ 80% since last letter`,
        myLetters: language === 'fr' ? 'Mes lettres de recommandation' : 'My Recommendation Letters',
        noLettersYet: language === 'fr'
            ? 'Aucune lettre de recommandation générée pour le moment.'
            : 'No recommendation letters generated yet.',
        getMyLetter: language === 'fr' ? 'Obtenir ma lettre' : 'Get my letter',
        startDescription: language === 'fr'
            ? 'Vérifie tes conditions d\'éligibilité et génère ta lettre officielle.'
            : 'Check your eligibility conditions and generate your official letter.',
    };

    const typeLabels = {
        complet: language === 'fr' ? 'Complète' : 'Complete',
        nieme: language === 'fr' ? 'Reprise' : 'Retake',
        nivels: language === 'fr' ? 'Partielle' : 'Partial'
    };

    // Eligibility & form content (rendered inside modal overlays)
    const formContent = (
        <>
            {/* Loading */}
            {loading && <LoadingSpinner />}

            {/* Error */}
            {error && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-2xl p-4 !mb-8 text-red-700 dark:text-red-400 text-sm font-medium">
                    {error}
                </div>
            )}

            {/* Eligibility & Form */}
            {eligibility && !loading && (
                <div className="grid grid-cols-1 lg:grid-cols-2 !gap-12">
                    {/* Left: Conditions */}
                    <div className={`${renderInModal ? '' : 'bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] shadow-sm dark:shadow-none'} p-8 space-y-6`}>
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-6 bg-green-500 rounded-full" />
                            <h3 className="text-lg font-black text-gray-900 dark:text-white">{t.conditionsTitle}</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ConditionCard
                                label={t.cond1}
                                met={eligibility.conditions.coursesCompleted.met}
                                progressText={`${eligibility.conditions.coursesCompleted.completed} / ${eligibility.conditions.coursesCompleted.total} ${language === 'fr' ? 'chapitres' : 'chapters'}`}
                            />
                            <ConditionCard
                                label={t.cond2}
                                met={eligibility.conditions.trainingHours.met}
                                progressText={`${eligibility.conditions.trainingHours.current}h / ${eligibility.conditions.trainingHours.required}h`}
                            />
                            <ConditionCard
                                label={t.cond3}
                                met={eligibility.conditions.allExamsCompleted.met}
                                progressText={`${eligibility.conditions.allExamsCompleted.completed} / ${eligibility.conditions.allExamsCompleted.total} ${language === 'fr' ? 'examens' : 'exams'}`}
                            />
                            <ConditionCard
                                label={t.cond4}
                                met={eligibility.conditions.highScoreExams.met}
                                progressText={`${eligibility.conditions.highScoreExams.count} / ${eligibility.conditions.highScoreExams.required}`}
                            />
                        </div>

                        {!eligibility.eligible && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center pt-2">
                                {t.notEligible}
                            </p>
                        )}
                    </div>

                    {/* Right: Form + Generate (only when eligible) */}
                    <div className="space-y-8">
                        {eligibility.eligible && (
                            <div className={`${renderInModal ? '' : 'bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] shadow-sm dark:shadow-none'} p-8 space-y-6`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-1.5 h-6 bg-green-500 rounded-full" />
                                    <h3 className="text-lg font-black text-gray-900 dark:text-white">{t.formTitle}</h3>
                                </div>

                                <div className="space-y-5">
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField label={t.firstName} value={firstName} onChange={setFirstName} required />
                                        <FormField label={t.lastName} value={lastName} onChange={setLastName} required />
                                    </div>

                                    <FormField
                                        label={t.medicalCert}
                                        value={medicalCertNumber}
                                        onChange={setMedicalCertNumber}
                                        required
                                        placeholder="CA-XXXXXX"
                                    />

                                    <div className="space-y-3">
                                        <label className="flex items-center gap-3 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                checked={hasValidPermit}
                                                onChange={e => setHasValidPermit(e.target.checked)}
                                                className="w-5 h-5 rounded border-gray-300 dark:border-white/10 text-green-600 focus:ring-green-500 bg-gray-50 dark:bg-[#1a1a1a]"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                                                {t.validPermit}
                                            </span>
                                        </label>

                                        {!hasValidPermit && (
                                            <FormField
                                                label={t.flyingHours}
                                                value={flyingHours}
                                                onChange={setFlyingHours}
                                                type="number"
                                                placeholder="0"
                                            />
                                        )}
                                    </div>

                                    {/* Letter type selector */}
                                    <div className="space-y-3">
                                        <label className="text-[11px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest ml-1">
                                            {t.letterType}
                                        </label>
                                        <div className="space-y-3">
                                            {/* Complet - first request */}
                                            {availableTypes.includes('complet') && (
                                                <label className={`block p-5 rounded-2xl border-2 cursor-pointer transition-colors ${
                                                    letterType === 'complet'
                                                        ? 'border-green-500 bg-green-50 dark:bg-green-500/5'
                                                        : 'border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-[#1a1a1a]'
                                                }`}>
                                                    <div className="flex items-center gap-3">
                                                        <input type="radio" name="letterType" value="complet"
                                                            checked={letterType === 'complet'} onChange={() => { setLetterType('complet'); setSelectedModules([]); }}
                                                            className="text-green-600 focus:ring-green-500" />
                                                        <span className="text-sm font-bold text-gray-900 dark:text-white">{t.typeComplet}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-7">
                                                        {language === 'fr'
                                                            ? 'Première demande de lettre de recommandation. Toutes les conditions ci-contre doivent être remplies.'
                                                            : 'First recommendation letter request. All conditions on the left must be met.'}
                                                    </p>
                                                </label>
                                            )}

                                            {/* Nieme - exam retake after failure */}
                                            {eligibility.nieme?.applicable && (
                                                <label className={`block p-5 rounded-2xl border-2 transition-colors ${
                                                    !eligibility.nieme?.eligible ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                                                } ${
                                                    letterType === 'nieme'
                                                        ? 'border-green-500 bg-green-50 dark:bg-green-500/5'
                                                        : 'border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-[#1a1a1a]'
                                                }`}>
                                                    <div className="flex items-center gap-3">
                                                        <input type="radio" name="letterType" value="nieme"
                                                            checked={letterType === 'nieme'}
                                                            disabled={!eligibility.nieme?.eligible}
                                                            onChange={() => { setLetterType('nieme'); setSelectedModules([]); }}
                                                            className="text-green-600 focus:ring-green-500" />
                                                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                            {language === 'fr' ? 'Examen complet — énième lettre' : 'Full exam — retake letter'}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-7">
                                                        {language === 'fr'
                                                            ? 'Dans le cas d\'un échec complet, vous devez effectuer une nouvelle demande. Vous devez obtenir de nouveau une note minimale de 80\u00a0% à au moins 3 examens complets.'
                                                            : 'In case of a complete failure, you must submit a new request. You need to achieve a minimum score of 80% on at least 3 complete exams again.'}
                                                    </p>
                                                    <p className={`text-xs font-bold mt-2 ml-7 ${
                                                        eligibility.nieme?.eligible ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                                                    }`}>
                                                        {t.niemeProgress(eligibility.nieme?.highScoreCountSinceLastLetter || 0)}
                                                    </p>
                                                </label>
                                            )}

                                            {/* Nivels - partial exam */}
                                            {availableTypes.includes('nivels') && (
                                                <label className={`block p-5 rounded-2xl border-2 cursor-pointer transition-colors ${
                                                    letterType === 'nivels'
                                                        ? 'border-green-500 bg-green-50 dark:bg-green-500/5'
                                                        : 'border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-[#1a1a1a]'
                                                }`}>
                                                    <div className="flex items-center gap-3">
                                                        <input type="radio" name="letterType" value="nivels"
                                                            checked={letterType === 'nivels'} onChange={() => setLetterType('nivels')}
                                                            className="text-green-600 focus:ring-green-500" />
                                                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                            {language === 'fr' ? 'Examen partiel' : 'Partial exam'}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-7">
                                                        {language === 'fr'
                                                            ? 'Si vous n\'avez validé que partiellement votre examen auprès de TC, vous pouvez demander une lettre pour un examen partiel. Vous devez obtenir une note minimale de 80\u00a0% à au moins 3 examens modules pour chaque module sélectionné.'
                                                            : 'If you only partially passed your exam with TC, you can request a partial exam letter. You must achieve a minimum score of 80% on at least 3 module exams for each selected module.'}
                                                    </p>
                                                </label>
                                            )}
                                        </div>
                                    </div>

                                    {/* Module selection for "nivels" type */}
                                    {letterType === 'nivels' && availableModules.length > 0 && (
                                        <div className="space-y-3">
                                            <label className="text-[11px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest ml-1">
                                                {t.selectModules}<span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                                {language === 'fr'
                                                    ? 'Obtenir une note minimale de 80% à au moins 3 examens par module'
                                                    : 'Achieve a minimum score of 80% on at least 3 exams per module'}
                                            </p>
                                            <div className="space-y-2">
                                                {availableModules.map(mod => {
                                                    const count = mod.highScoreCount ?? 0;
                                                    const required = mod.required ?? 3;
                                                    const isEligible = mod.eligible !== false && count >= required;
                                                    return (
                                                        <label key={mod.categoryKey}
                                                            className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                                                                !isEligible
                                                                    ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-[#1a1a1a]'
                                                                    : selectedModules.includes(mod.categoryKey)
                                                                        ? 'cursor-pointer border-green-400 dark:border-green-500/40 bg-green-50 dark:bg-green-500/5'
                                                                        : 'cursor-pointer border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-[#1a1a1a]'
                                                            }`}>
                                                            <input type="checkbox"
                                                                checked={selectedModules.includes(mod.categoryKey)}
                                                                disabled={!isEligible}
                                                                onChange={e => {
                                                                    if (e.target.checked) {
                                                                        setSelectedModules(prev => [...prev, mod.categoryKey]);
                                                                    } else {
                                                                        setSelectedModules(prev => prev.filter(m => m !== mod.categoryKey));
                                                                    }
                                                                }}
                                                                className="w-4 h-4 rounded border-gray-300 dark:border-white/10 text-green-600 focus:ring-green-500" />
                                                            <span className="text-sm text-gray-900 dark:text-white flex-1">
                                                                {language === 'fr' ? mod.labelFr : mod.labelEn}
                                                            </span>
                                                            <span className={`text-xs font-bold ${isEligible ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                                                ({count}/{required})
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="pt-2">
                                    <button
                                        onClick={handleGenerate}
                                        disabled={!formValid || submitting}
                                        className={`w-full py-4 rounded-full font-black text-sm shadow-xl transition-all ${
                                            formValid && !submitting
                                                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-green-500/10 hover:scale-[1.02] active:scale-[0.98]'
                                                : 'bg-gray-200 dark:bg-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                                        }`}
                                    >
                                        {submitting ? t.generating : t.generate}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Per-level history - only in external modal mode (LevelOverview) */}
                        {eligibility.previousLetters?.length > 0 && isModal && (
                            <div className="p-8 space-y-5">
                                <div className="flex items-center gap-3">
                                    <div className="w-1.5 h-6 bg-green-500 rounded-full" />
                                    <h3 className="text-lg font-black text-gray-900 dark:text-white">{t.historyTitle}</h3>
                                </div>

                                <div className="space-y-3">
                                    {eligibility.previousLetters.map(letter => (
                                        <div
                                            key={letter.idLetter}
                                            className="flex items-center justify-between bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-xl px-5 py-3.5"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FileIcon className="w-5 h-5 text-green-500" />
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                                                        {letter.firstName} {letter.lastName}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(letter.createdAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                                                        {' — '}
                                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                                            letter.type === 'complet'
                                                                ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                                                                : letter.type === 'nieme'
                                                                    ? 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400'
                                                                    : 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400'
                                                        }`}>
                                                            {typeLabels[letter.type] || letter.type}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleViewLetter(letter.idLetter)}
                                                className="text-xs font-bold text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 border border-green-200 dark:border-green-500/30 rounded-lg px-4 py-2 hover:bg-green-50 dark:hover:bg-green-500/10 transition-colors"
                                            >
                                                {t.view}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );

    // Modal wrapper (shared between external modal and page form modal)
    const renderModalOverlay = (onCloseHandler) => (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={onCloseHandler}
        >
            <div
                className="bg-white dark:bg-gray-900 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-200 custom-scrollbar"
                onClick={e => e.stopPropagation()}
            >
                {/* Sticky title bar */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 rounded-t-3xl px-8 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                            <RecommendationIcon className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-black text-gray-900 dark:text-white">{t.title}</h2>
                        {selectedLevel && (
                            <div
                                className="ml-2 w-8 h-8 rounded-lg flex items-center justify-center text-[9px] font-black shadow-sm"
                                style={{ backgroundColor: getLevelColor(selectedLevel), color: '#000' }}
                            >
                                {selectedLevel.toUpperCase()}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onCloseHandler}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
                    >
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Modal content */}
                <div className="p-6 md:p-8">
                    {formContent}
                </div>
            </div>
        </div>
    );

    // External modal mode (called from LevelOverview)
    if (isModal) {
        return renderModalOverlay(onClose);
    }

    // Page mode - overview hub
    return (
        <div className="pb-12">
            {/* Header Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#1b5e20] via-[#2e7d32] to-[#004d40] border border-white/5 !mb-12 shadow-xl">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }} />

                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-green-600 rounded-2xl shadow-lg shadow-green-500/20">
                            <RecommendationIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">{t.title}</h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-md leading-snug">{t.subtitle}</p>
                </div>

                <div className="absolute right-[-40px] bottom-[-40px] opacity-20">
                    <RecommendationIcon className="w-80 h-80 text-green-500 stroke-[0.5]" />
                </div>
            </div>

            {/* Level Selector Bar - always show even with one level */}
            {availableLevels.length === 0 ? (
                <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[1.5rem] p-8 text-center !mb-12 shadow-sm dark:shadow-none">
                    <p className="text-gray-500 dark:text-gray-400">{t.noLevels}</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[1.5rem] p-6 !mb-12 shadow-sm dark:shadow-none">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">
                        {language === 'fr' ? 'Niveaux disponibles' : 'Available Levels'}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {availableLevels.map(([code, data]) => (
                            <button
                                key={code}
                                onClick={() => setSelectedLevel(code)}
                                className={`rounded-2xl px-4 py-3 flex items-center gap-3 transition-all border-2 ${
                                    selectedLevel === code
                                        ? 'border-gray-900 dark:border-white shadow-lg scale-[1.02]'
                                        : 'border-transparent opacity-70 hover:opacity-100 hover:scale-[1.02]'
                                }`}
                            >
                                <div className="relative">
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center text-[11px] font-black shadow-md"
                                        style={{ backgroundColor: getLevelColor(code), color: '#000' }}
                                    >
                                        {code.toUpperCase()}
                                    </div>
                                    {data.isActive && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-[#121212]" />
                                    )}
                                </div>
                                <div className="text-left">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white block">
                                        {data.labelFr || code.toUpperCase()}
                                    </span>
                                    <span className={`text-[10px] font-medium ${data.isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {data.isActive
                                            ? (language === 'fr' ? `${data.remainingDays}j restants` : `${data.remainingDays}d left`)
                                            : (language === 'fr' ? 'Non abonné' : 'Not subscribed')
                                        }
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Overview: Letters History + Get Letter CTA */}
            <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] shadow-sm dark:shadow-none overflow-hidden">
                {/* History section */}
                <div className="p-8 space-y-6">
                    <div className="flex items-center gap-3">
                        <div className="w-1.5 h-6 bg-green-500 rounded-full" />
                        <h3 className="text-lg font-black text-gray-900 dark:text-white">{t.myLetters}</h3>
                    </div>

                    {historyLoading ? (
                        <LoadingSpinner />
                    ) : allLetters.length === 0 ? (
                        <div className="text-center py-10">
                            <div className="mx-auto w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-4">
                                <FileIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                            </div>
                            <p className="text-gray-400 dark:text-gray-500 text-sm">{t.noLettersYet}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {allLetters.map(letter => (
                                <div
                                    key={letter.idLetter}
                                    className="flex items-center gap-4 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-2xl p-5 hover:border-green-300 dark:hover:border-green-500/30 transition-colors"
                                >
                                    {/* Level badge */}
                                    <div
                                        className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center text-[10px] font-black shadow-md"
                                        style={{ backgroundColor: getLevelColor(letter.levelVar), color: '#000' }}
                                    >
                                        {letter.levelVar?.toUpperCase()}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                {letter.firstName} {letter.lastName}
                                            </span>
                                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                                letter.type === 'complet'
                                                    ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                                                    : letter.type === 'nieme'
                                                        ? 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400'
                                                        : 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400'
                                            }`}>
                                                {typeLabels[letter.type] || letter.type}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {new Date(letter.createdAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                                day: 'numeric', month: 'long', year: 'numeric'
                                            })}
                                            {' — '}
                                            {getLevelLabel(letter.levelVar)}
                                        </p>
                                    </div>

                                    {/* View button */}
                                    <button
                                        onClick={() => handleViewLetter(letter.idLetter)}
                                        className="flex-shrink-0 text-xs font-bold text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 border border-green-200 dark:border-green-500/30 rounded-xl px-4 py-2.5 hover:bg-green-50 dark:hover:bg-green-500/10 transition-colors"
                                    >
                                        {t.view}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* CTA: Obtenir ma lettre */}
                {selectedLevel && (() => {
                    return (
                        <div className="border-t border-gray-200 dark:border-white/5 p-8">
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                <div className="flex-1 text-center sm:text-left">
                                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                                        {t.getMyLetter}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {t.startDescription}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowFormModal(true)}
                                    className="flex items-center gap-3 px-8 py-4 rounded-full font-black text-sm transition-all bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-xl shadow-green-500/20 hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    <RecommendationIcon className="w-5 h-5" />
                                    {t.getMyLetter}
                                    <span className="ml-1 px-2 py-0.5 rounded-lg text-[10px] font-black bg-white/20">
                                        {selectedLevel.toUpperCase()}
                                    </span>
                                </button>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Form Modal (opened from page) */}
            {showFormModal && renderModalOverlay(handleCloseFormModal)}
        </div>
    );
};

export default RecommendationLetter;
